import { Component, OnDestroy, OnInit } from '@angular/core';
import { RestaurantFinanceService } from './service/restaurant-finance.service';
import { RestaurantInfoService } from '../../../services/restaurant-info/restaurant-info.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { RestaurantResponse } from 'libs/shared-models/src/lib/restaurant/restaurant-response';
import { ConnectElementTagName, loadConnectAndInitialize } from '@stripe/connect-js';
import { environment } from 'apps/restaurant/src/environments/environment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Constants } from '../../../utils/constants';
import { RestaurantFinancialStatus } from './model/restaurant-financial-status-response';
import { LOADING_STYLE } from 'libs/shared-ui/src/lib/fs-loading-spinner/fs-loading-spinner.component';

@UntilDestroy()
@Component({
  selector: 'web-foodis-section-finance',
  templateUrl: './section-finance.component.html',
  styleUrls: ['./section-finance.component.scss'],
})
export class SectionFinanceComponent implements OnInit, OnDestroy {

  public restaurantData$: Observable<RestaurantResponse> = this.restaurantInfoService.getState$();
  public financialData$ = this.restaurantFinanceService.getFinancialData$();

  public FinancialStatus: typeof RestaurantFinancialStatus = RestaurantFinancialStatus;
  public LOADING_STYLE: typeof LOADING_STYLE = LOADING_STYLE;

  public isLoadingFinancialData$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isLoadingCreateStripeID$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isLoadingStripeSession$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public showStripeSession$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private HTML_STRIPE_CONTAINER = "stripe-session-load-container";
  private lastLoadedStripeAccountId: string = "";

  constructor(
    private restaurantFinanceService: RestaurantFinanceService,
    private restaurantInfoService: RestaurantInfoService
  ) {  
  } 

  public ngOnInit() {
    this.restaurantData$.pipe(untilDestroyed(this)).subscribe((resData) => {
      const currentStripeAccountId = resData?.stripeAccountId || "";

      if (!currentStripeAccountId) {
        this.lastLoadedStripeAccountId = "";
        this.showStripeSession$.next(false);
        this.clearStripeSessionContainer();
        this.restaurantFinanceService.resetFinancialData();
        this.restaurantFinanceService.resetSessionData();
        return;
      }

      if (this.lastLoadedStripeAccountId !== currentStripeAccountId) {
        this.lastLoadedStripeAccountId = currentStripeAccountId;
        this.refreshFinancialStatus();
      }
    })
  }

  public onPressCreate() {
    this.isLoadingCreateStripeID$.next(true);
    this.restaurantFinanceService.requestStripeAccountCreation$().pipe(untilDestroyed(this)).subscribe((success) => {
      // re-fetch restaurant info data (so it gets the new assigned stripeAccountId)
      this.isLoadingCreateStripeID$.next(false);
      if (success) {
        this.restaurantInfoService.fetchRestaurantAPI();
      }
    });
  }

  public refreshFinancialStatus() {
    this.isLoadingFinancialData$.next(true);

    this.restaurantFinanceService.requestFinancialStatus$().pipe(untilDestroyed(this)).subscribe((_success) => {
      this.isLoadingFinancialData$.next(false);
    });
  }

  public onOpenStripeOnboarding() {
    this.showStripeSession$.next(true);
    this.isLoadingStripeSession$.next(true);
    this.clearStripeSessionContainer();

    this.restaurantFinanceService.requestStripeAccountSession$().pipe(untilDestroyed(this)).subscribe((success) => {
      this.isLoadingStripeSession$.next(false);
      if (success) {
        this.loadStripeSessionUIEmbedded();
      }
    });
  }

  public getFinancialStatusKey(value: RestaurantFinancialStatus | undefined | null): string {
    switch (value) {
      case RestaurantFinancialStatus.ACTIVE:
        return "finance_status_active";
      case RestaurantFinancialStatus.RESTRICTED:
        return "finance_status_restricted";
      case RestaurantFinancialStatus.ONBOARDING_REQUIRED:
        return "finance_status_onboarding_required";
      default:
        return "finance_status_not_connected";
    }
  }

  public getFinancialStatusClass(value: RestaurantFinancialStatus | undefined | null): string {
    switch (value) {
      case RestaurantFinancialStatus.ACTIVE:
        return "active";
      case RestaurantFinancialStatus.RESTRICTED:
        return "restricted";
      case RestaurantFinancialStatus.ONBOARDING_REQUIRED:
        return "onboarding";
      default:
        return "not-connected";
    }
  }

  public getBooleanValueKey(value: boolean): string {
    return value ? "finance_state_enabled" : "finance_state_disabled";
  }

  public formatRequirementValue(value: string): string {
    if (!value) {
      return "";
    }

    return value
      .replace(/\./g, " > ")
      .replace(/_/g, " ");
  }

  private loadStripeSessionUIEmbedded() {
    const stripeAccountSessionClientSecret = async () => {
      return this.restaurantFinanceService.getSessionData().clientSecret;
    };

    const stripeConnectInstance = loadConnectAndInitialize({
      publishableKey: environment.STRIPE_PUBLISHABLE_KEY,
      fetchClientSecret: stripeAccountSessionClientSecret
    });

    const paymentComponent = stripeConnectInstance.create(Constants.STRIPE_ONBOARDING_COMPONENT as ConnectElementTagName);
    const container = document.getElementById(this.HTML_STRIPE_CONTAINER);
    container?.appendChild(paymentComponent);
  }

  private clearStripeSessionContainer() {
    const container = document.getElementById(this.HTML_STRIPE_CONTAINER);
    if (container) {
      container.innerHTML = "";
    }
  }

  public ngOnDestroy(): void {
    this.clearStripeSessionContainer();
    this.restaurantFinanceService.resetSessionData();
    this.restaurantFinanceService.resetFinancialData();
  }
}
